/**
 * Mapa canônico das 8 fases do funil (DOCUMENTACAO.md §1.3) — fonte única no frontend.
 * Liga o enum DealStage do backend (serializado como string, ex.: "AnaliseIC1") ao rótulo,
 * abreviação, limite de aging (dias, vindo do mock) e ao valor numérico do enum — este último
 * é o que vai no corpo do MoveDealStage, porque o backend não tem JsonStringEnumConverter
 * (bind de enum por número). Ordem fixa, espelhando o enum.
 */
export interface StageDef {
  /** Nome do enum no backend (DealStage.ToString()). Chave de ligação com a API. */
  apiName: string;
  /** Valor numérico do enum (enviado no corpo do move). */
  apiValue: number;
  /** Id curto interno (espelha o mock). */
  id: string;
  /** Rótulo completo exibido no cabeçalho da coluna / pílula. */
  label: string;
  /** Abreviação. */
  short: string;
  /** Dias até a fase ser considerada estagnada; null = sem limite (Investimento). */
  agingThreshold: number | null;
}

export const STAGES: readonly StageDef[] = [
  { apiName: 'Originacao', apiValue: 1, id: 'orig', label: 'Originação', short: 'Orig.', agingThreshold: 7 },
  { apiName: 'Triagem', apiValue: 2, id: 'triag', label: 'Triagem', short: 'Triag.', agingThreshold: 14 },
  { apiName: 'AnaliseIC1', apiValue: 3, id: 'ic1', label: 'Análise / IC1', short: 'IC1', agingThreshold: 30 },
  { apiName: 'Proposta', apiValue: 4, id: 'prop', label: 'Proposta', short: 'Prop.', agingThreshold: 30 },
  { apiName: 'AnaliseIC2', apiValue: 5, id: 'ic2', label: 'Análise / IC2', short: 'IC2', agingThreshold: 30 },
  { apiName: 'DueDiligence', apiValue: 6, id: 'dd', label: 'Due Diligence', short: 'DD', agingThreshold: 60 },
  { apiName: 'Closing', apiValue: 7, id: 'closing', label: 'Closing', short: 'Closing', agingThreshold: 30 },
  { apiName: 'Investimento', apiValue: 8, id: 'invest', label: 'Investimento', short: 'Invest.', agingThreshold: null },
];

/**
 * Fases renderizadas no board do pipeline (lote 1, item 5): exclui **Investimento**. A fase 8 foi
 * descontinuada do funil — o caminho para "investido" passa a ser exclusivamente o handoff →
 * Portfólio (a coluna era redundante com o Portfólio). `STAGES` continua com as 8 fases para
 * lookups/rótulo/aging/ordenação (stageByApiName/stageLabel/stageOrder), que outras telas usam.
 */
export const BOARD_STAGES: readonly StageDef[] = STAGES.filter((s) => s.apiName !== 'Investimento');

const STAGE_BY_API = new Map(STAGES.map((s) => [s.apiName, s]));

export function stageByApiName(apiName: string): StageDef | undefined {
  return STAGE_BY_API.get(apiName);
}

/** Rótulo da fase a partir do nome do enum; cai para o próprio nome se desconhecido. */
export function stageLabel(apiName: string): string {
  return STAGE_BY_API.get(apiName)?.label ?? apiName;
}

/** Posição da fase na ordem do funil (para ordenação da tabela). -1 se desconhecida. */
export function stageOrder(apiName: string): number {
  return STAGES.findIndex((s) => s.apiName === apiName);
}

/**
 * Data efetiva de "entrada na fase" para "dias nesta fase" / "Entrou na fase". Para a **Originação**
 * usa SEMPRE a entrada inicial no funil (InitialEntryAtUtc), inclusive se o deal voltou à Originação —
 * o tempo conta da data real de entrada, não da inserção/reentrada. Demais fases: StageEnteredAtUtc.
 * Não altera dado persistido nem o threshold de aging (Originação = 7 dias segue); muda só a base.
 */
export function effectiveStageEnteredAtUtc(d: {
  stage: string;
  stageEnteredAtUtc: string;
  initialEntryAtUtc: string;
}): string {
  return d.stage === 'Originacao' ? d.initialEntryAtUtc : d.stageEnteredAtUtc;
}

type AgingSortable = { stage: string; stageEnteredAtUtc: string; initialEntryAtUtc: string };

/**
 * Comparador "mais tempo sem alteração primeiro": ordena pelo aging efetivo (dias na fase atual)
 * de forma decrescente — o deal parado há mais tempo vem no topo. Baseia-se em
 * effectiveStageEnteredAtUtc (timestamp ISO em UTC): quanto mais antigo o timestamp, maior o aging,
 * então ordenar ascendente por timestamp = decrescente por dias.
 */
export function compareByStalest(a: AgingSortable, b: AgingSortable): number {
  return effectiveStageEnteredAtUtc(a).localeCompare(effectiveStageEnteredAtUtc(b));
}
